import * as React from "react";

import { createStyles, makeStyles } from "@material-ui/styles";
import {
    Page,
    PageSection,
    RelodeTheme,
    SelectInput,
    Text,
    TextInput,
    useAsyncEffect,
} from "../../../components";
import { MessageRecipient } from "../../messages";
import { getRecipients } from "../../messages/messaging.service";
import { createError, RelodeApiError } from "../../../modules";
import { TimeZone } from "../../../domain";
import { InterviewNotificationEmailFormProps } from "./InterviewNotificationEmailFormProps";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                flexBasis: "50%",
            },
            input: {
                margin: `${theme.spacing(3)}px 0 0 0`,
            },
            pageSection: {
                padding: theme.spacing(4),
            },
        }),
    { name: "InterviewNotificationEmailForm" }
);

export const InterviewNotificationEmailForm = (
    props: InterviewNotificationEmailFormProps
) => {
    const classes = useStyles({});
    const {
        initialRecipients,
        candidateUserId,
        jobId,
        interviewStartDateTime,
        interviewTimeZoneId,
        selectedRecipients,
        setSelectedRecipients,
        notificationSubject,
        setNotificationSubject,
        inputError,
        isMobile,
    } = props;

    const [suggestedRecipients, setSuggestedRecipients] = React.useState<
        MessageRecipient[]
    >([]);

    useAsyncEffect(async () => {
        // this code assumes there are no duplicates returned from the API
        try {
            const recs = await getRecipients({
                leadId: candidateUserId,
                jobId: jobId || null,
            });

            if (initialRecipients) {
                const init = initialRecipients.map(r => {
                    const match = recs.find(m => m.userId === r.userId);
                    return match ?? r;
                });
                setSelectedRecipients(init);
            }

            setSuggestedRecipients(recs);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort() || err.isClientError()) return;
            }
            throw createError(
                err,
                "core.messageComposer.useAsyncEffect: get and set recipients"
            );
        }
    }, [candidateUserId, jobId]);

    React.useEffect(() => {
        const date = interviewStartDateTime.split(" ")[0];
        const convertedDate = new Date(date);
        const weekday = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
        ];
        const month = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];

        setNotificationSubject(
            "UPCOMING INTERVIEW:" +
                ` ${weekday[convertedDate.getDay()]}, ${
                    month[convertedDate.getMonth()]
                } ${date.split("/")[1]}` +
                ` ${interviewStartDateTime.split(" ").slice(1).join(" ")} ${
                    TimeZone.fromId(interviewTimeZoneId).abbreviation
                }`.toUpperCase()
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateUserId, jobId]);

    function handleTextFieldChange(
        e: React.ChangeEvent<HTMLInputElement>
    ): void {
        setNotificationSubject(e.target.value);
    }

    if (isMobile)
        return (
            <>
                <Text variant="h4">message details</Text>
                <SelectInput<MessageRecipient, true, true>
                    multiple
                    mode="list"
                    label="to"
                    id="submitCandidateRecipientsList"
                    helperText={
                        inputError.recipientsError
                            ? inputError.recipientsError
                            : null
                    }
                    error={!!inputError.recipientsError}
                    options={suggestedRecipients}
                    value={selectedRecipients}
                    onChange={(e, v: MessageRecipient[]) =>
                        setSelectedRecipients(v)
                    }
                    getOptionLabel={o => `${o.displayName} (${o.roleName})`}
                />
                <TextInput
                    fullWidth
                    className={classes.input}
                    label="subject"
                    id="submitCandidateEmailSubjectValue"
                    name="subject"
                    value={notificationSubject}
                    onChange={handleTextFieldChange}
                    helperText={
                        inputError.subjectError
                            ? inputError.subjectError
                            : null
                    }
                    error={!!inputError.subjectError}
                />
            </>
        );
    else
        return (
            <Page
                id="interviewNotificationEmailForm"
                showHeader={false}
                pageType="secondary"
                className={classes.root}
            >
                <PageSection className={classes.pageSection}>
                    <Text variant="h4">message details</Text>
                    <SelectInput<MessageRecipient, true, true>
                        multiple
                        mode="list"
                        label="to"
                        id="submitCandidateRecipientsList"
                        helperText={
                            inputError.recipientsError
                                ? inputError.recipientsError
                                : null
                        }
                        error={!!inputError.recipientsError}
                        options={suggestedRecipients}
                        value={selectedRecipients}
                        onChange={(e, v: MessageRecipient[]) =>
                            setSelectedRecipients(v)
                        }
                        getOptionLabel={o => `${o.displayName} (${o.roleName})`}
                    />
                    <TextInput
                        fullWidth
                        className={classes.input}
                        label="subject"
                        id="submitCandidateEmailSubjectValue"
                        name="subject"
                        value={notificationSubject}
                        onChange={handleTextFieldChange}
                        helperText={
                            inputError.subjectError
                                ? inputError.subjectError
                                : null
                        }
                        error={!!inputError.subjectError}
                    />
                </PageSection>
            </Page>
        );
};
